import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { status } from 'constants/general';
import Loader from 'modules/common/components/Loader';
import { orderStatusEnum } from 'modules/orders/constants';
import { resetInsuranceOrderEprescription } from 'modules/orders/actions';
import { rxDiscountLineKeyFromPrescriptionMedicineItem } from 'modules/orders/medicalInsuranceDiscountCalculate';
import {
  formatInsuranceOrderLine1,
  formatInsuranceOrderLine2,
} from './insuranceOrderEprescriptionFormatters';
import {
  InsuranceOrderCard,
  InsuranceOrderCount,
  InsuranceOrderHeader,
  InsuranceOrderIngredient,
  InsuranceOrderMessage,
  InsuranceOrderMetaLine,
  InsuranceOrderName,
  InsuranceOrderDiscountPrices,
  InsuranceOrderPriceBeforeStruck,
  InsuranceOrderPriceAfter,
  InsuranceOrderNotes,
  InsuranceOrderRow,
  InsuranceOrderTextCol,
  InsuranceOrderThumb,
  InsuranceOrderTitle,
} from './InsuranceOrderSection.style';

const InsuranceOrderSection = ({
  strings,
  orderDetails,
  orderId,
  insuranceRxLineDiscountLookup,
}) => {
  const dispatch = useDispatch();
  const eprescription = useSelector(state => state.pharmacyOrders.eprescriptionInsuranceOrder);
  const orderStateTypeId = orderDetails?.details?.orderStateTypeId;
  const orderLoadStatus = orderDetails?.status;

  useEffect(() => {
    const shouldFetch =
      orderLoadStatus === status.SUCCESS && orderStateTypeId === orderStatusEnum.Viewed && orderId;

    if (!shouldFetch) {
      dispatch(resetInsuranceOrderEprescription());
    }
  }, [dispatch, orderId, orderLoadStatus, orderStateTypeId]);

  if (orderStateTypeId !== orderStatusEnum.Viewed) {
    return null;
  }

  const { status: eprescriptionStatus, details, errorMessage } = eprescription || {};
  const medicineItems = details?.data?.prescriptionMedicineItems ?? [];

  return (
    <InsuranceOrderCard>
      <InsuranceOrderHeader>
        <InsuranceOrderTitle>{strings.insuranceOrderSectionTitle}</InsuranceOrderTitle>
        <InsuranceOrderCount>
          {eprescriptionStatus === status.SUCCESS
            ? strings.insuranceOrderItemsCount(medicineItems.length)
            : ''}
        </InsuranceOrderCount>
      </InsuranceOrderHeader>

      {eprescriptionStatus === status.FETCHING && (
        <div style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
          <Loader width={32} height={32} thickness={4} />
        </div>
      )}

      {eprescriptionStatus === status.FAIL && (
        <InsuranceOrderMessage>
          {errorMessage || strings.insuranceOrderLoadFailed}
        </InsuranceOrderMessage>
      )}

      {eprescriptionStatus === status.SUCCESS && medicineItems.length === 0 && (
        <InsuranceOrderMessage>{strings.insuranceOrderEmpty}</InsuranceOrderMessage>
      )}

      {eprescriptionStatus === status.SUCCESS &&
        medicineItems.map(item => {
          const line1 = formatInsuranceOrderLine1(item);
          const line2 = formatInsuranceOrderLine2(item);
          const notes = item?.notes?.trim();
          const key = item.itemKey || `${item.id}-${item.productShapeId}`;
          const rxDiscountKey = rxDiscountLineKeyFromPrescriptionMedicineItem(item);
          const discountPrices =
            rxDiscountKey && insuranceRxLineDiscountLookup
              ? insuranceRxLineDiscountLookup[rxDiscountKey]
              : null;
          const showDiscountPrices =
            discountPrices &&
            discountPrices.priceBefore != null &&
            discountPrices.priceAfter != null;

          return (
            <InsuranceOrderRow key={key}>
              {item.imageURL ? (
                <InsuranceOrderThumb src={item.imageURL} alt={item.name || ''} />
              ) : (
                <div style={{ width: 48, flexShrink: 0 }} />
              )}
              <InsuranceOrderTextCol>
                <InsuranceOrderName>{item.name}</InsuranceOrderName>
                {item.activeIngredient ? (
                  <InsuranceOrderIngredient>{item.activeIngredient}</InsuranceOrderIngredient>
                ) : null}
                {line1 ? <InsuranceOrderMetaLine>{line1}</InsuranceOrderMetaLine> : null}
                {line2 ? <InsuranceOrderMetaLine>{line2}</InsuranceOrderMetaLine> : null}
                {showDiscountPrices ? (
                  <InsuranceOrderDiscountPrices>
                    <InsuranceOrderPriceBeforeStruck>
                      {discountPrices.priceBefore.toFixed(2)} {strings.orderInvoiceServicesCurrency}
                    </InsuranceOrderPriceBeforeStruck>
                    <InsuranceOrderPriceAfter>
                      {discountPrices.priceAfter.toFixed(2)} {strings.orderInvoiceServicesCurrency}
                    </InsuranceOrderPriceAfter>
                  </InsuranceOrderDiscountPrices>
                ) : null}
                {notes ? (
                  <InsuranceOrderNotes>
                    {strings.additionalInstructionsLabel}: {notes}
                  </InsuranceOrderNotes>
                ) : null}
              </InsuranceOrderTextCol>
            </InsuranceOrderRow>
          );
        })}
    </InsuranceOrderCard>
  );
};

InsuranceOrderSection.propTypes = {
  strings: PropTypes.shape({
    insuranceOrderSectionTitle: PropTypes.string.isRequired,
    insuranceOrderItemsCount: PropTypes.func.isRequired,
    insuranceOrderLoadFailed: PropTypes.string.isRequired,
    insuranceOrderEmpty: PropTypes.string.isRequired,
    orderInvoiceServicesCurrency: PropTypes.string.isRequired,
    additionalInstructionsLabel: PropTypes.string.isRequired,
  }).isRequired,
  orderDetails: PropTypes.shape({
    status: PropTypes.string,
    details: PropTypes.shape({
      orderStateTypeId: PropTypes.number,
    }),
  }).isRequired,
  orderId: PropTypes.string,
  insuranceRxLineDiscountLookup: PropTypes.objectOf(
    PropTypes.shape({
      priceBefore: PropTypes.number,
      priceAfter: PropTypes.number,
    }),
  ),
};

InsuranceOrderSection.defaultProps = {
  orderId: null,
  insuranceRxLineDiscountLookup: null,
};

export default InsuranceOrderSection;
